import { useCallback, useEffect, useMemo, useState } from "react"

import { App, Flex, Pagination, Spin, Typography } from "antd"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../Components/Button"
import EmptyComponent from "../../Components/Empty"
import ExportDropdown from "../../Components/Export"
import Filters from "../../Components/Filters"
import { SubtitleSection } from "../../Components/SubtitleSection/SubtitleSection"
import Table from "../../Components/Table"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import { modalConfig } from "../../theme"
import { serializeData } from "../../Utils/utils"
import { usePageQuery } from "../BusinessSettings/usePageQuery"
import { InvoiceDrawer } from "./components/Drawer"
import InvoiceHistoryCard from "./components/InvoiceHistoryCard"
import { getActionColumn, getColumns, getDefaultColumn } from "./components/Table/columns"
import Creators, { selectAreFiltersDefault } from "./reducer"

import "./styles.less"

dayjs.extend(utc)

const pageSize = 10

const { Title, Text } = Typography

const InvoiceHistoryBlock = ({
  isMobile,
  isAdmin,
  theme,
  defaultColumns,
  currentBusinessAccountId,
  columnsAdmin,
  fetching,
  result,
  exportFetching,
  exportResult,
  generateInvoice,
  forUser,
  userViewForAdmin,
  personalSetting,
  userId,

  filters,
  filterOptions,
  visibleFiltersUser,
  visibleFiltersAdmin,
  isLoyaltyBilling,
  statusOptions,
  businessAccount,
  isBusinessSettings,
  ...props
}) => {
  //const navigate = useNavigate()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { search } = useLocation()
  const { user_id } = usePageQuery(search)
  const { notification, modal } = App.useApp()
  const externalDemoMode = useExternalDemoMode()

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)
  const [showAll, setShowAll] = useState(false)

  const isFiltersDefault = useSelector(selectAreFiltersDefault)

  const { count, results } = result

  const visibleFilters = isAdmin ? visibleFiltersAdmin : visibleFiltersUser
  const myColumns = isAdmin ? columnsAdmin : defaultColumns

  const myColumnsLength = myColumns.filter(({ active }) => active).length
  const myColumnsHash = myColumns
    .filter(({ active }) => active)
    .map(({ field_id }) => field_id)
    .join("")

  const businessAccountId = isAdmin ? businessAccount?.id : currentBusinessAccountId
  const isLoyaltyBillingForAdmin = isAdmin ? isLoyaltyBilling : false

  const notificationConfig = {
    closeIcon: <Text>Dismiss</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  const updateInfo = () => {
    const serializeFilters = serializeData(filters)
    const columns = myColumns.filter(({ active }) => active).map(({ field_id }) => field_id)
    const data = {
      page: pageNumber,
      page_size: pageSize,
      ordering,
      ...serializeFilters,
      // ...((user_id || personalSetting) ? {user_id: user_id || userId} : {business_account_id: businessAccountId}),
      ...(isBusinessSettings
        ? { business_account_id: businessAccountId }
        : { user_id: user_id || userId }),
      ...(columns.length && {
        columns: ["id", "status", "brand", ...columns].join(",")
      })
    }
    props.getInvoices({ ...data })
  }

  useEffect(() => {
    props.getInvoicesFilterOptions()
    if (isAdmin) props.getInvoicesStatusOptions()
  }, [])

  useEffect(() => {
    if (businessAccountId && pageNumber !== 1) setPageNumber(1)
  }, [businessAccountId])

  useEffect(() => {
    updateInfo()
  }, [ordering, pageNumber, myColumnsLength, filters, businessAccountId])

  useEffect(() => {
    if (exportResult) {
      notification.success({
        message: exportResult?.type || "Exporting",
        description: exportResult?.message || "Invoice will be downloaded shortly.",
        ...notificationConfig
      })
      props.clearExportResult()
    }
  }, [exportResult])

  /*useEffect(() => {
    if (generateInvoice) {
      notification.success({
        message: 'Generated',
        description: generateInvoice?.message,
        ...notificationConfig,
      })
      props.clearInvoiceGenerating()
    }
  }, [generateInvoice])*/

  function handleSelectRow(data, type) {
    if (type === "view_details") {
      if (data?.id) {
        if (!forUser && search) {
          props.setInvoiceDetails(data)
          navigate(`${search}&open_invoice=${data?.id}`)
        } else {
          props.setInvoiceDetails(data)
          navigate(`?open_invoice=${data?.id}`)
        }
      }
    } else if (type === "download_invoice") {
      props.getInvoiceDownload({
        file_name: "Invoice",
        invoice_id: data?.id,
        format: ".pdf"
      })
    }
  }

  const tableSettings = useMemo(
    () => ({
      scroll: {
        x: "100%",
        y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px))",
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      },
      locale: {
        emptyText: !count ? (
          <EmptyComponent type="no_data_available" />
        ) : (
          <EmptyComponent type="search" />
        )
      }
    }),
    [myColumnsLength, count, pageNumber]
  )

  const changeStatusConfirm = (data) => {
    modal.confirm({
      ...modalConfig("exclamation"),
      centered: !isMobile,

      title: t("Confirm status changing"),
      content: (
        <>
          <Text type="secondary">{`Are you sure you want to set ${data?.status} status for this invoice?`}</Text>
          <br />
          <Text type="secondary">This action can NOT be undone.</Text>
        </>
      ),
      cancelText: "Cancel",
      okText: "Confirm",
      //cancelButtonProps: {size: 'large'},
      okButtonProps: {
        ...modalConfig("exclamation")?.okButtonProps,
        danger: true,
        ghost: true
      },
      onOk() {
        props.updateInvoice({ ...data, type_request: "all_invoices" })
      }
    })
  }

  const getTableColumns = useMemo(() => {
    const handleChangeStatus = ({ id, newStatus }) => {
      changeStatusConfirm({ id, status: newStatus })
    }

    let options = statusOptions || []

    let allColumns = getColumns({
      theme,
      options,
      handleChangeStatus,
      isLoyaltyBillingForAdmin
    })

    const actionColumn = getActionColumn({
      visible: {
        download_invoice: !isAdmin,
        view_details: !(personalSetting || user_id)
      },
      onSelectRow: handleSelectRow,
      externalDemoMode
    })

    return [
      ...myColumns
        .filter(({ active }) => active)
        .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data })),
      ...(actionColumn ? [actionColumn] : [])
    ]
  }, [myColumnsHash, isLoyaltyBillingForAdmin, statusOptions])

  const handleChangeTable = useCallback((pagination, _tableFilters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  const handleExport = () => {
    if (userViewForAdmin) return
    if (businessAccountId || user_id || userId) {
      if (forUser) {
        props.getInvoicesExportForUser({
          file_name: "Invoice report",
          ...(user_id || personalSetting
            ? { user_id: user_id || userId }
            : { business_account_id: businessAccountId }),
          type: "for_user"
        })
      } else {
        if (businessAccountId) {
          props.getInvoicesExport({
            file_name: "Invoice report",
            business_account_id: businessAccountId
          })
        }
      }
    }
  }

  const handleChangeFilter = useCallback((newData, type) => {
    if (type === "changeVisible") {
      props.changeVisibleFilters(isAdmin ? "visibleFiltersAdmin" : "visibleFilters", newData)
    } else {
      props.changeFilters(newData)
      setPageNumber(1)
    }
  }, [])

  const onClear = useCallback(() => {
    props.clearInvoicesFilters()
  }, [])

  if (isMobile) {
    const allMobileData = results ?? []
    const mobileData = showAll ? allMobileData : allMobileData.slice(0, 4)

    return (
      <>
        <SubtitleSection
          isMobile
          title={t("Invoice history")}
          description={t("Review all invoices and track their payment statuses.")}
        />

        <ExportDropdown
          loading={exportFetching}
          onExport={handleExport}
          isAdmin={false}
          disabled={!allMobileData?.length || userViewForAdmin}
          block
          fullWidths
          style={{ marginBottom: 16 }}
        />

        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptions}
          allFilters={myColumns}
          filtersType={"invoice_history"}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          showClearBtn={!isFiltersDefault}
          onClearFilters={onClear}
          isMobile={isMobile}
        />

        <Spin spinning={fetching}>
          <Flex vertical gap={12} style={{ marginTop: 16 }}>
            {!fetching && !allMobileData?.length && <EmptyComponent type="no_data_available" />}
            {mobileData.map((record, i) => (
              <InvoiceHistoryCard
                key={record?.id ?? i}
                record={record}
                theme={theme}
                isAdmin={isAdmin}
                externalDemoMode={externalDemoMode}
                onDownload={(r) => handleSelectRow(r, "download_invoice")}
              />
            ))}
          </Flex>
        </Spin>

        {count > 4 && !showAll && (
          <Flex justify="center" style={{ marginTop: 16 }}>
            <ButtonComponent onClick={() => setShowAll(true)} text={t("View all activity list")} />
          </Flex>
        )}

        {showAll && count > pageSize && (
          <Flex justify="center" style={{ marginTop: 16 }}>
            <Pagination
              total={count}
              current={pageNumber}
              pageSize={pageSize}
              onChange={(page) => {
                setPageNumber(page)
                setOrdering(null)
              }}
              showSizeChanger={false}
            />
          </Flex>
        )}

        <InvoiceDrawer userViewForAdmin={userViewForAdmin} />
      </>
    )
  }

  return (
    <>
      <Flex justify="space-between">
        <SubtitleSection
          title={t("Invoice history")}
          description={t("Review all invoices and track their payment statuses.")}
        />

        {/*{
          (isAdmin && !user_id) && <ButtonComponent
            style={{marginLeft: 'auto', marginRight: 10}}
            onClick={() => props.invoiceGenerating({business_account_id: businessAccountId})}
          >
            Invoice generating
          </ButtonComponent>
        }*/}

        <ExportDropdown
          loading={exportFetching}
          onExport={handleExport}
          isAdmin={false}
          disabled={!results?.length || userViewForAdmin}
        />
      </Flex>

      <Flex gap={32} align="center" justify="space-between" style={{ margin: "-4px 0 24px" }}>
        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptions}
          allFilters={myColumns}
          filtersType={"invoice_history"}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          showClearBtn={!isFiltersDefault}
          onClearFilters={onClear}
        />
      </Flex>

      <Table
        columns={getTableColumns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        //withOutPagination={true}
        locale={tableSettings.locale}
      />

      <InvoiceDrawer userViewForAdmin={userViewForAdmin} />
    </>
  )
}

const mapStateToProps = (state) => ({
  currentBusinessAccountId: state.auth.userInfo.current_business_account_id,
  userId: state.auth.userInfo?.id,
  isAdmin: state.auth.isAdmin,
  isMobile: state.nav.isMobile,
  theme: state.nav.theme,

  defaultColumns: state.invoices.defaultColumns,

  columnsAdmin: state.invoices.columnsAdmin,

  fetching: state.invoices.fetching,
  result: state.invoices.result,

  exportFetching: state.invoices.exportFetching,
  exportResult: state.invoices.exportResult,
  generateInvoice: state.invoices.generateInvoice,

  filters: state.invoices.filters,
  filterOptions: state.invoices.filterOptions,
  visibleFiltersUser: state.invoices.visibleFilters,
  visibleFiltersAdmin: state.invoices.visibleFiltersAdmin,
  statusOptions: state.invoices.statusOptions,

  isLoyaltyBilling: state.balance?.balance?.["is_loyalty_billing"],

  businessAccount: state.user.businessAccount
})

const mapDispatchToProps = (dispatch) => ({
  getInvoices: (params) => dispatch(Creators.getInvoicesRequest(params)),
  //reissueInvoice: (id) => dispatch(Creators.reissueInvoiceRequest(id)),
  updateInvoice: (data) => dispatch(Creators.updateInvoiceRequest(data)),

  getInvoicesExportForUser: (data) => dispatch(Creators.getInvoicesExportForUserRequest(data)),
  getInvoicesExport: (data) => dispatch(Creators.getInvoicesExportRequest(data)),
  getInvoiceDownload: (data) => dispatch(Creators.getInvoiceDownloadRequest(data)),
  clearExportResult: () => dispatch(Creators.invoicesSetProp("exportResult", null)),
  setInvoiceDetails: (data) => dispatch(Creators.invoicesSetProp("invoiceDetails", data)),

  //invoiceGenerating: (data) => dispatch(Creators.invoiceGeneratingRequest(data)),
  //clearInvoiceGenerating: () => dispatch(Creators.invoicesSetProp('generateInvoice', null)),

  getInvoicesFilterOptions: (params) => dispatch(Creators.getInvoicesFilterOptionsRequest(params)),
  changeFilters: (filters) => dispatch(Creators.invoicesSetProp("filters", filters)),
  changeVisibleFilters: (visibleFiltersType, filters) =>
    dispatch(Creators.invoicesSetProp(visibleFiltersType, filters)),
  clearInvoicesFilters: () => dispatch(Creators.clearInvoicesFilters()),

  getInvoicesStatusOptions: (params) => dispatch(Creators.getInvoicesStatusOptionsRequest(params))
})

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceHistoryBlock)
